/**
 * Public Report API
 * Client-side API for public issue reporting via QR codes
 *
 * Provides both admin functions (token management) and public functions
 * (issue submission) via a single Cloud Function callable.
 *
 * P2-10: Includes runtime response validation
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { recordActivity } from './activityApi';
import {
    CreateTokenRequest,
    RevokeTokenRequest,
    GetTokenForBuildingRequest,
    GetAllActiveTokensRequest,
    ValidateTokenRequest,
    GetUploadUrlRequest,
    SubmitReportRequest,
    CreateTokenResponse,
    RevokeTokenResponse,
    GetTokenForBuildingResponse,
    GetAllActiveTokensResponse,
    ActiveTokenWithBuilding,
    ValidateTokenResponse,
    GetUploadUrlResponse,
    SubmitReportResponse,
    PublicReportPhoto,
    PublicPrivacyPolicy,
} from '../types/publicReport';

/**
 * Union type for all API requests
 */
type PublicReportApiRequest =
    | CreateTokenRequest
    | RevokeTokenRequest
    | GetTokenForBuildingRequest
    | GetAllActiveTokensRequest
    | ValidateTokenRequest
    | GetUploadUrlRequest
    | SubmitReportRequest;

/**
 * Union type for all API responses
 */
type PublicReportApiResponse =
    | CreateTokenResponse
    | RevokeTokenResponse
    | GetTokenForBuildingResponse
    | GetAllActiveTokensResponse
    | ValidateTokenResponse
    | GetUploadUrlResponse
    | SubmitReportResponse;

// ============================================================================
// P2-10: Response Validators
// ============================================================================

/**
 * Validate CreateTokenResponse
 */
const validateCreateTokenResponse = (data: unknown): CreateTokenResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true || typeof d.token !== 'string' || typeof d.reportUrl !== 'string') {
        throw new Error('Ogiltigt svar från servern');
    }
    return { success: true, token: d.token, reportUrl: d.reportUrl };
};

/**
 * Validate RevokeTokenResponse
 */
const validateRevokeTokenResponse = (data: unknown): RevokeTokenResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true) {
        throw new Error('Ogiltigt svar från servern');
    }
    return { success: true };
};

/**
 * Validate GetTokenForBuildingResponse
 */
const validateGetTokenForBuildingResponse = (data: unknown): GetTokenForBuildingResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true) {
        throw new Error('Ogiltigt svar från servern');
    }
    // token can be null or an object
    const token = d.token === null ? null : d.token;
    if (token !== null && typeof token !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    // reportUrl can be null or string
    const reportUrl = d.reportUrl === null ? null : d.reportUrl;
    if (reportUrl !== null && typeof reportUrl !== 'string') {
        throw new Error('Ogiltigt svar från servern');
    }
    return {
        success: true,
        token: token as GetTokenForBuildingResponse['token'],
        reportUrl: reportUrl as string | null,
    };
};

/**
 * Validate GetAllActiveTokensResponse
 */
const validateGetAllActiveTokensResponse = (data: unknown): GetAllActiveTokensResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true || !Array.isArray(d.tokens)) {
        throw new Error('Ogiltigt svar från servern');
    }
    // Validate each token in the array
    const tokens: ActiveTokenWithBuilding[] = d.tokens.map((t: unknown) => {
        if (!t || typeof t !== 'object') {
            throw new Error('Ogiltigt svar från servern');
        }
        const token = t as Record<string, unknown>;
        if (
            typeof token.token !== 'string' ||
            typeof token.buildingKey !== 'string' ||
            typeof token.buildingName !== 'string' ||
            typeof token.createdBy !== 'string' ||
            typeof token.createdAt !== 'string' ||
            typeof token.reportUrl !== 'string'
        ) {
            throw new Error('Ogiltigt svar från servern');
        }
        return {
            token: token.token,
            buildingKey: token.buildingKey,
            buildingName: token.buildingName,
            buildingAddress: typeof token.buildingAddress === 'string' ? token.buildingAddress : undefined,
            createdBy: token.createdBy,
            createdAt: token.createdAt,
            reportUrl: token.reportUrl,
            publicPlacement: token.publicPlacement === true,
        };
    });
    return { success: true, tokens };
};

/**
 * Validate ValidateTokenResponse
 */
const validateValidateTokenResponse = (data: unknown): ValidateTokenResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true || typeof d.buildingName !== 'string') {
        throw new Error('Ogiltigt svar från servern');
    }
    return {
        success: true,
        buildingName: d.buildingName,
        buildingAddress: typeof d.buildingAddress === 'string' ? d.buildingAddress : undefined,
        publicPlacement: d.publicPlacement === true,
        privacyPolicy: d.publicPlacement === true ? parsePrivacyPolicy(d.privacyPolicy) : null,
    };
};

/**
 * The policy as the report page may show it: an https URL and/or a
 * non-empty text, or null when neither is usable.
 */
export const parsePrivacyPolicy = (data: unknown): PublicPrivacyPolicy | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    const url = typeof d.url === 'string' && d.url.startsWith('https://') ? d.url : undefined;
    const text = typeof d.text === 'string' && d.text.trim() ? d.text : undefined;
    if (!url && !text) return null;
    return { ...(url && { url }), ...(text && { text }) };
};

/**
 * Validate GetUploadUrlResponse
 */
const validateGetUploadUrlResponse = (data: unknown): GetUploadUrlResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (
        d.success !== true ||
        typeof d.uploadUrl !== 'string' ||
        typeof d.key !== 'string' ||
        typeof d.storagePath !== 'string'
    ) {
        throw new Error('Ogiltigt svar från servern');
    }
    return { success: true, uploadUrl: d.uploadUrl, key: d.key, storagePath: d.storagePath };
};

/**
 * Validate SubmitReportResponse
 */
const validateSubmitReportResponse = (data: unknown): SubmitReportResponse => {
    if (!data || typeof data !== 'object') {
        throw new Error('Ogiltigt svar från servern');
    }
    const d = data as Record<string, unknown>;
    if (d.success !== true || typeof d.message !== 'string') {
        throw new Error('Ogiltigt svar från servern');
    }
    return { success: true, message: d.message };
};

/**
 * API client for public issue reporting
 */
class PublicReportApi {
    private async call(request: PublicReportApiRequest): Promise<unknown> {
        const callable = httpsCallable<PublicReportApiRequest, PublicReportApiResponse>(
            functions,
            'publicReportApi'
        );
        const result = await callable(request);
        return result.data;
    }

    // ============================================================================
    // Admin Actions (require authentication)
    // ============================================================================

    /**
     * Create a new token for a building
     * Revokes any existing active token for the building. A code for a public
     * place needs the admin's acknowledgement of the customer's responsibility.
     */
    async createToken(
        buildingKey: string,
        placement: { publicPlacement: boolean; acknowledged: boolean } = { publicPlacement: false, acknowledged: false }
    ): Promise<CreateTokenResponse> {
        if (placement.publicPlacement && !placement.acknowledged) {
            throw new Error('Bekräfta ansvaret för personuppgifter för en kod på allmän plats');
        }
        const data = await this.call({
            action: 'createToken',
            buildingKey,
            ...(placement.publicPlacement && { publicPlacement: true, acknowledged: true }),
        });
        const response = validateCreateTokenResponse(data);
        recordActivity('settings.updated', { buildingKey });
        return response;
    }

    /**
     * Revoke an existing token
     */
    async revokeToken(token: string): Promise<RevokeTokenResponse> {
        const data = await this.call({
            action: 'revokeToken',
            token,
        });
        const response = validateRevokeTokenResponse(data);
        // No targetKey: the token is a secret.
        recordActivity('settings.updated');
        return response;
    }

    /**
     * Get the active token for a building (if any)
     */
    async getTokenForBuilding(buildingKey: string): Promise<GetTokenForBuildingResponse> {
        const data = await this.call({
            action: 'getTokenForBuilding',
            buildingKey,
        });
        return validateGetTokenForBuildingResponse(data);
    }

    /**
     * Get all active tokens across all buildings
     */
    async getAllActiveTokens(): Promise<GetAllActiveTokensResponse> {
        const data = await this.call({
            action: 'getAllActiveTokens',
        });
        return validateGetAllActiveTokensResponse(data);
    }

    // ============================================================================
    // Public Actions (no authentication required)
    // ============================================================================

    /**
     * Validate a token and get building info
     */
    async validateToken(token: string): Promise<ValidateTokenResponse> {
        const data = await this.call({
            action: 'validateToken',
            token,
        });
        return validateValidateTokenResponse(data);
    }

    /**
     * Get a signed upload URL for a photo
     */
    async getUploadUrl(
        token: string,
        issueKey: string,
        fileName: string,
        mimeType: string
    ): Promise<GetUploadUrlResponse> {
        const data = await this.call({
            action: 'getUploadUrl',
            token,
            issueKey,
            fileName,
            mimeType,
        });
        return validateGetUploadUrlResponse(data);
    }

    /**
     * Submit a public issue report
     */
    async submitReport(
        token: string,
        issueKey: string,
        description: string,
        photos?: PublicReportPhoto[]
    ): Promise<SubmitReportResponse> {
        const data = await this.call({
            action: 'submitReport',
            token,
            issueKey,
            description,
            photos,
        });
        return validateSubmitReportResponse(data);
    }
}

/**
 * Singleton instance of the public report API
 */
export const publicReportApi = new PublicReportApi();
